/**
 * Floating "Lesson Map" control + slide-in navigation panel (left edge in RTL, right in LTR).
 */

import React, { useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, CheckCircle2, ChevronDown, GraduationCap, Layers, Loader2, Lock, Map, MapPin, X } from 'lucide-react';
import { useAppLocale } from '../../providers/LocaleProvider';
import { PersianText } from '../shared/PersianText';
import { mirrorRtlDecor } from '../../utils/mirrorRtlDecor';
import type { LessonMapData, LessonMapItem, LessonMapSection } from '../../utils/lessonMapSections';
import { isProductTourUiNode } from '../../learnerTour/productTourUi';
import { SubscriptionPlanIcons } from '../shared/SubscriptionPlanIcons';
import { CollapsibleFadePanel } from '../shared/CollapsibleFadePanel';
import type { SubscriptionPlan } from '../../types/subscription';
import {
  TOUR_TARGET_BLUR_EVENT,
  TOUR_TARGET_FOCUS_EVENT,
  type TourTargetEventDetail,
} from '../../learnerTour/tourTargetEvents';
import { LEARNER_UI_ANIM_MS, useModalBackdropFade } from '../../utils/learnerSlideModalMotion';
import {
  clampLessonMapBubbleY,
  defaultLessonMapBubbleY,
  readLessonMapBubbleY,
  readLessonMapPanelOpen,
  writeLessonMapBubbleY,
  writeLessonMapPanelOpen,
} from '../../utils/lessonMapPanelStorage';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';

const PANEL_ANIM_MS = LEARNER_UI_ANIM_MS;
const BUBBLE_SLIDE_IN_MS = LEARNER_UI_ANIM_MS;
const BUBBLE_SLIDE_OUT_MS = LEARNER_UI_ANIM_MS;
/** Border travels to the next lesson, then the badge fades in and the frame grows. */
const FOCUS_MOVE_MS = 420;
const FOCUS_GROW_MS = 360;
/** Wait just long enough for the bubble to start leaving; do not wait out the full slide-out. */
const PANEL_OPEN_DELAY_MS = Math.round(LEARNER_UI_ANIM_MS * 0.4);

/** Width of the timeline gutter; line + dots are centered within it. */
const SPINE_GUTTER_CLASS = 'w-[18px]';

type LessonMapFocusBox = {
  top: number;
  left: number;
  width: number;
  height: number;
  passed: boolean;
  animate: boolean;
};

function findCurrentMapId(sections: LessonMapSection[]): string | null {
  for (const section of sections) {
    const current = section.items.find((item) => item.isCurrent);
    if (current) return current.id;
    if (section.children?.length) {
      const nested = findCurrentMapId(section.children);
      if (nested) return nested;
    }
  }
  return null;
}

function measureLessonMapFocusBox(
  root: HTMLElement,
  expandBadge = false
): Omit<LessonMapFocusBox, 'animate'> | null {
  const card = root.querySelector<HTMLElement>('[data-lesson-map-card][data-lesson-map-current-card="true"]');
  if (!card) return null;
  const rootRect = root.getBoundingClientRect();
  const rect = card.getBoundingClientRect();
  let height = rect.height;
  if (expandBadge) {
    const inner = card.querySelector<HTMLElement>('[data-lesson-map-here-inner]');
    const slot = card.querySelector<HTMLElement>('[data-lesson-map-here-slot]');
    if (inner && slot) {
      height += Math.max(0, inner.scrollHeight - slot.getBoundingClientRect().height);
    }
  }
  return {
    top: rect.top - rootRect.top,
    left: rect.left - rootRect.left,
    width: rect.width,
    height,
    passed: card.dataset.lessonMapPassed === 'true'
  };
}

/** True once the moving border has landed and the "you are here" badge may appear. */
const LessonMapHereContext = React.createContext(true);

const LESSON_MAP_STYLES = `
  @keyframes lesson-map-panel-in {
    from { transform: translateX(100%); opacity: 0.6; }
    to { transform: translateX(0); opacity: 1; }
  }
  @keyframes lesson-map-panel-out {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(100%); opacity: 0.6; }
  }
  @keyframes lesson-map-here-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.45); }
    50% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.12); }
  }
  .lesson-map-panel-enter {
    animation: lesson-map-panel-in ${PANEL_ANIM_MS}ms ease-out forwards;
  }
  .lesson-map-panel-exit {
    animation: lesson-map-panel-out ${PANEL_ANIM_MS}ms ease-out forwards;
  }
  @keyframes lesson-map-panel-in-rtl {
    from { transform: translateX(-100%); opacity: 0.6; }
    to { transform: translateX(0); opacity: 1; }
  }
  @keyframes lesson-map-panel-out-rtl {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(-100%); opacity: 0.6; }
  }
  .lesson-map-panel-enter-rtl {
    animation: lesson-map-panel-in-rtl ${PANEL_ANIM_MS}ms ease-out forwards;
  }
  .lesson-map-panel-exit-rtl {
    animation: lesson-map-panel-out-rtl ${PANEL_ANIM_MS}ms ease-out forwards;
  }
  @keyframes lesson-map-here-pulse-green {
    0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.45); }
    50% { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.12); }
  }
  @keyframes lesson-map-focus-land {
    0% { transform: scale(0.985); filter: brightness(0.98); }
    55% { transform: scale(1.02); filter: brightness(1.03); }
    100% { transform: scale(1); filter: brightness(1); }
  }
  .lesson-map-you-are-here {
    animation: lesson-map-here-pulse 2.4s ease-in-out infinite;
  }
  .lesson-map-you-are-here-green {
    animation: lesson-map-here-pulse-green 2.4s ease-in-out infinite;
  }
  .lesson-map-focus-land {
    animation: lesson-map-focus-land 200ms ease-out;
  }
  .lesson-map-focus-box.is-moving {
    transition:
      top ${FOCUS_MOVE_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
      left ${FOCUS_MOVE_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
      width ${FOCUS_MOVE_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
      height ${FOCUS_MOVE_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .lesson-map-focus-box.is-growing {
    transition:
      top ${FOCUS_GROW_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
      left ${FOCUS_GROW_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
      width ${FOCUS_GROW_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
      height ${FOCUS_GROW_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .lesson-map-here-slot {
    transition:
      grid-template-rows ${FOCUS_GROW_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity ${FOCUS_GROW_MS}ms ease-out;
  }
  .lesson-map-here-slot[data-open="false"] {
    transition: none;
  }
  .lesson-map-dot-shift {
    transition:
      margin-top ${FOCUS_GROW_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
      transform ${FOCUS_GROW_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
      background-color ${FOCUS_GROW_MS}ms ease-out,
      box-shadow ${FOCUS_GROW_MS}ms ease-out;
  }
  .lesson-map-focus-box[data-passed="false"] {
    border-color: rgb(96 165 250);
    background-color: transparent;
  }
  .lesson-map-focus-box[data-passed="true"] {
    border-color: rgb(74 222 128);
    background-color: transparent;
  }
  .dark .lesson-map-focus-box[data-passed="false"] {
    border-color: rgb(96 165 250);
    background-color: transparent;
  }
  .dark .lesson-map-focus-box[data-passed="true"] {
    border-color: rgb(74 222 128);
    background-color: transparent;
  }
  @media (prefers-reduced-motion: reduce) {
    .lesson-map-focus-land {
      animation: none;
    }
    .lesson-map-focus-box.is-moving,
    .lesson-map-focus-box.is-growing,
    .lesson-map-here-slot,
    .lesson-map-dot-shift {
      transition: none;
    }
  }
  .lesson-map-bubble-btn {
    isolation: isolate;
    touch-action: none;
    background: radial-gradient(
      circle at 32% 26%,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(224, 231, 255, 0.92) 34%,
      rgba(199, 210, 254, 0.95) 68%,
      rgba(165, 180, 252, 0.98) 100%
    );
    box-shadow:
      0 3px 12px rgba(79, 70, 229, 0.18),
      0 1px 3px rgba(79, 70, 229, 0.1),
      inset 0 1px 4px rgba(255, 255, 255, 0.7),
      inset 0 -2px 6px rgba(67, 56, 202, 0.08);
    border: 1px solid rgba(99, 102, 241, 0.35);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }
  .lesson-map-bubble-btn[data-bubble-motion="in"] {
    transition:
      transform ${BUBBLE_SLIDE_IN_MS}ms ease-out,
      opacity ${BUBBLE_SLIDE_IN_MS}ms ease-out,
      right ${PANEL_ANIM_MS}ms ease-out,
      top 0ms;
  }
  .lesson-map-bubble-btn[data-bubble-motion="out"] {
    transition:
      transform ${BUBBLE_SLIDE_OUT_MS}ms ease-out,
      opacity ${BUBBLE_SLIDE_OUT_MS}ms ease-out,
      right ${PANEL_ANIM_MS}ms ease-out,
      top 0ms;
  }
  .lesson-map-bubble-btn.is-dragging {
    transition: transform 80ms linear, opacity 80ms linear, right 0ms;
    cursor: grabbing;
  }
  .lesson-map-bubble-btn::before {
    content: '';
    position: absolute;
    top: 10%;
    left: 14%;
    width: 36%;
    height: 26%;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.38) 0%, rgba(255, 255, 255, 0.04) 72%, transparent 100%);
    pointer-events: none;
    z-index: 1;
  }
  .lesson-map-bubble-btn::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(circle at 72% 80%, rgba(255, 255, 255, 0.14) 0%, transparent 48%);
    pointer-events: none;
    z-index: 1;
  }
  .lesson-map-bubble-btn:hover {
    box-shadow:
      0 4px 14px rgba(79, 70, 229, 0.12),
      0 1px 4px rgba(79, 70, 229, 0.06),
      inset 0 1px 5px rgba(255, 255, 255, 0.26),
      inset 0 -2px 7px rgba(67, 56, 202, 0.08);
  }
  .lesson-map-bubble-btn:active {
    box-shadow:
      0 2px 6px rgba(79, 70, 229, 0.08),
      inset 0 1px 4px rgba(255, 255, 255, 0.16),
      inset 0 -1px 4px rgba(67, 56, 202, 0.08);
  }
  .lesson-map-backdrop.keyboard-slide-modal-backdrop {
    transition-duration: ${PANEL_ANIM_MS}ms;
  }
  .dark .lesson-map-bubble-btn {
    background: radial-gradient(
      circle at 32% 26%,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(129, 140, 248, 0.1) 38%,
      rgba(67, 56, 202, 0.16) 72%,
      rgba(49, 46, 129, 0.22) 100%
    );
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow:
      0 3px 14px rgba(0, 0, 0, 0.16),
      0 1px 4px rgba(79, 70, 229, 0.08),
      inset 0 1px 4px rgba(255, 255, 255, 0.06),
      inset 0 -2px 6px rgba(30, 27, 75, 0.14);
  }
  .dark .lesson-map-bubble-btn:hover {
    box-shadow:
      0 4px 16px rgba(0, 0, 0, 0.2),
      0 1px 5px rgba(79, 70, 229, 0.1),
      inset 0 1px 5px rgba(255, 255, 255, 0.08),
      inset 0 -2px 7px rgba(30, 27, 75, 0.16);
  }
`;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]')
  );
}

export interface LessonMapPanelProps {
  mapData: LessonMapData;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onNavigate: (item: LessonMapItem) => void;
  accentColor?: string;
  persistOpenKey?: string;
  subscriptionPlans?: SubscriptionPlan[];
  /** Matches `data-tour-id` on the floating map toggle button. */
  toggleTourId?: string;
  /** Matches `data-tour-id` on the slide-in map panel; tour focus opens the panel. */
  panelTourId?: string;
  /** Override panel chrome strings (defaults to lessonMap.* keys). */
  labels?: {
    title?: string;
    openAria?: string;
    closeAria?: string;
    bubbleTitle?: string;
    navAria?: string;
    empty?: string;
  };
  /**
   * Smoothly animate current-item changes (bullet color, focused card, badge).
   * Advanced lesson/tag maps and quiz/exercise maps use this. Simple theme leaves it off.
   */
  animateFocusChange?: boolean;
}

export const LessonMapPanel: React.FC<LessonMapPanelProps> = ({
  mapData,
  loading = false,
  error = null,
  onRetry,
  onNavigate,
  persistOpenKey,
  subscriptionPlans = [],
  toggleTourId,
  panelTourId,
  labels,
  animateFocusChange = false,
}) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const panelTitle = labels?.title ?? t('lessonMap.title');
  const openAria = labels?.openAria ?? t('lessonMap.openAria');
  const closeAria = labels?.closeAria ?? t('lessonMap.closeAria');
  const bubbleTitle = labels?.bubbleTitle ?? t('lessonMap.bubbleTitle');
  const navAria = labels?.navAria ?? t('lessonMap.navAria');
  const emptyLabel = labels?.empty ?? t('lessonMap.empty');
  const [open, setOpen] = useState(() => readLessonMapPanelOpen(persistOpenKey));
  const [exiting, setExiting] = useState(false);
  const [mounted, setMounted] = useState(() => readLessonMapPanelOpen(persistOpenKey));
  /** Independent of panel mount so the bubble can finish sliding out before the panel enters. */
  const [bubbleOffscreen, setBubbleOffscreen] = useState(() => readLessonMapPanelOpen(persistOpenKey));
  const panelRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const mapContentRef = useRef<HTMLDivElement>(null);
  const focusBoxRef = useRef<HTMLDivElement>(null);
  const focusCurrentIdRef = useRef<string | null>(null);
  const currentMapId = findCurrentMapId(mapData.sections);
  /** Badge stays hidden until the border has finished moving onto this lesson. */
  const [readyHereId, setReadyHereId] = useState<string | null>(currentMapId);
  const hereReady = !animateFocusChange || (currentMapId != null && readyHereId === currentMapId);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistOpenKeyRef = useRef(persistOpenKey);
  const panelId = useId();
  const [bubbleY, setBubbleY] = useState(() => {
    const saved = readLessonMapBubbleY();
    return saved != null ? clampLessonMapBubbleY(saved) : defaultLessonMapBubbleY();
  });
  const [bubbleDragging, setBubbleDragging] = useState(false);
  const bubbleDragRef = useRef<{
    pointerId: number;
    startY: number;
    startBubbleY: number;
    moved: boolean;
  } | null>(null);
  /** Swallow the synthetic click after pointer-up (tap or drag end). */
  const suppressBubbleClickRef = useRef(false);

  useEffect(() => {
    const onResize = () => {
      setBubbleY((y) => clampLessonMapBubbleY(y));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const clearExitTimer = useCallback(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
  }, []);

  const closePanel = useCallback(() => {
    if (!open || exiting) return;
    clearOpenTimer();
    setExiting(true);
    exitTimerRef.current = setTimeout(() => {
      setOpen(false);
      setExiting(false);
      setMounted(false);
      setBubbleOffscreen(false);
      exitTimerRef.current = null;
    }, PANEL_ANIM_MS);
  }, [open, exiting, clearOpenTimer]);

  useHistoryBackClose(open, closePanel, 'lesson-map');

  const openPanel = useCallback(
    (opts?: { immediate?: boolean }) => {
      if (open && !exiting) return;
      const wasExiting = exiting;
      clearExitTimer();
      clearOpenTimer();
      setExiting(false);
      setBubbleOffscreen(true);

      // Canceling a close: the panel is still mounted — restore it without waiting on the bubble.
      if (wasExiting || opts?.immediate) {
        setMounted(true);
        setOpen(true);
        return;
      }

      openTimerRef.current = setTimeout(() => {
        setMounted(true);
        setOpen(true);
        openTimerRef.current = null;
      }, PANEL_OPEN_DELAY_MS);
    },
    [open, exiting, clearExitTimer, clearOpenTimer]
  );

  const tourWasOpenRef = useRef<boolean | null>(null);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (!panelTourId) return undefined;
    const onFocus = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      if (detail?.targetKey !== panelTourId) return;
      tourWasOpenRef.current = openRef.current;
      if (!openRef.current) openPanel({ immediate: true });
    };
    const onBlur = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      if (detail?.targetKey !== panelTourId) return;
      if (tourWasOpenRef.current === false) closePanel();
      tourWasOpenRef.current = null;
    };
    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    window.addEventListener(TOUR_TARGET_BLUR_EVENT, onBlur);
    return () => {
      window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
      window.removeEventListener(TOUR_TARGET_BLUR_EVENT, onBlur);
    };
  }, [panelTourId, openPanel, closePanel]);

  const togglePanel = useCallback(() => {
    if (open || exiting) {
      closePanel();
      return;
    }
    if (openTimerRef.current) {
      clearOpenTimer();
      setBubbleOffscreen(false);
      return;
    }
    openPanel();
  }, [open, exiting, closePanel, openPanel, clearOpenTimer]);

  const handleBubblePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (bubbleOffscreen) return;
    bubbleDragRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startBubbleY: bubbleY,
      moved: false
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleBubblePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = bubbleDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dy = e.clientY - drag.startY;
    if (Math.abs(dy) > 5) {
      drag.moved = true;
      setBubbleDragging(true);
    }
    if (!drag.moved) return;
    setBubbleY(clampLessonMapBubbleY(drag.startBubbleY + dy));
  };

  const finishBubbleDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = bubbleDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (drag.moved) {
      const nextY = clampLessonMapBubbleY(drag.startBubbleY + (e.clientY - drag.startY));
      setBubbleY(nextY);
      writeLessonMapBubbleY(nextY);
    } else {
      togglePanel();
    }
    suppressBubbleClickRef.current = true;
    bubbleDragRef.current = null;
    setBubbleDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* capture already released */
    }
  };

  const handleBubbleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (suppressBubbleClickRef.current) {
      suppressBubbleClickRef.current = false;
      e.preventDefault();
      return;
    }
    togglePanel();
  };

  useEffect(() => {
    if (persistOpenKeyRef.current === persistOpenKey) return;
    persistOpenKeyRef.current = persistOpenKey;
    const saved = readLessonMapPanelOpen(persistOpenKey);
    clearExitTimer();
    clearOpenTimer();
    setExiting(false);
    setOpen(saved);
    setMounted(saved);
    setBubbleOffscreen(saved);
  }, [persistOpenKey, clearExitTimer, clearOpenTimer]);

  useEffect(() => {
    if (exiting) return;
    writeLessonMapPanelOpen(persistOpenKey, open);
  }, [open, exiting, persistOpenKey]);

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open || loading) return;
    const t = window.setTimeout(() => {
      const current = scrollRef.current?.querySelector('[data-lesson-map-current="true"]');
      current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, PANEL_ANIM_MS + 40);
    return () => window.clearTimeout(t);
  }, [open, loading, mapData]);

  useLayoutEffect(() => {
    const box = focusBoxRef.current;
    if (!animateFocusChange || !open) {
      focusCurrentIdRef.current = null;
      return undefined;
    }
    const root = mapContentRef.current;
    if (!root || !box) return undefined;

    const paint = (rect: Omit<LessonMapFocusBox, 'animate'>) => {
      box.dataset.passed = rect.passed ? 'true' : 'false';
      box.classList.toggle('lesson-map-you-are-here', !rect.passed);
      box.classList.toggle('lesson-map-you-are-here-green', rect.passed);
      box.style.opacity = '1';
      box.style.top = `${rect.top}px`;
      box.style.left = `${rect.left}px`;
      box.style.width = `${rect.width}px`;
      box.style.height = `${rect.height}px`;
    };

    const card = root.querySelector<HTMLElement>('[data-lesson-map-card][data-lesson-map-current-card="true"]');
    const nextId = card?.dataset.lessonMapId ?? null;
    const moved = focusCurrentIdRef.current != null && nextId != null && focusCurrentIdRef.current !== nextId;
    focusCurrentIdRef.current = nextId;
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const badgeSettled = nextId != null && readyHereId === nextId;
    const next = measureLessonMapFocusBox(root, badgeSettled);
    if (!next || !nextId) {
      box.style.opacity = '0';
      return undefined;
    }

    const previousHeight = Number.parseFloat(box.style.height || '0');
    const growing =
      badgeSettled &&
      !moved &&
      !reduced &&
      previousHeight > 0 &&
      next.height - previousHeight > 1;

    if (growing) {
      box.classList.remove('is-moving', 'is-growing');
      void box.offsetHeight;
      box.classList.add('is-growing');
      void box.offsetHeight;
      paint(next);
      const done = window.setTimeout(() => box.classList.remove('is-growing'), FOCUS_GROW_MS);
      return () => window.clearTimeout(done);
    }

    if (!moved || reduced) {
      if (readyHereId !== nextId) setReadyHereId(nextId);
      box.classList.remove('is-moving', 'is-growing');
      paint(next);
      return undefined;
    }

    // Slide the border onto the lesson while it is still compact, then reveal the badge.
    box.classList.remove('is-moving', 'is-growing');
    void box.offsetHeight;
    box.classList.add('is-moving');
    void box.offsetHeight;
    paint(next);

    const settle = window.setTimeout(() => {
      box.classList.remove('is-moving');
      setReadyHereId(nextId);
    }, FOCUS_MOVE_MS);
    return () => window.clearTimeout(settle);
  }, [animateFocusChange, open, mapData, readyHereId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (open) {
          closePanel();
          return;
        }
        if (openTimerRef.current) {
          clearOpenTimer();
          setBubbleOffscreen(false);
          return;
        }
      }
      if (e.key.toLowerCase() !== 'm' || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      togglePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closePanel, togglePanel, clearOpenTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-lesson-map-toggle]')) return;
      if (isProductTourUiNode(target)) return;
      closePanel();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, closePanel]);

  const handleNavigate = (item: LessonMapItem) => {
    if (item.isCurrent || item.isLocked) return;
    onNavigate(item);
  };

  const countMapItems = (sections: LessonMapSection[]): { total: number; completed: number } => {
    let total = 0;
    let completed = 0;
    for (const section of sections) {
      total += section.items.length;
      completed += section.items.filter((i) => i.isCompleted).length;
      if (section.children?.length) {
        const nested = countMapItems(section.children);
        total += nested.total;
        completed += nested.completed;
      }
    }
    return { total, completed };
  };

  const { total: totalItems, completed: completedItems } = countMapItems(mapData.sections);
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  const panelVisible = mounted && (open || exiting);
  const backdropVisible = useModalBackdropFade(panelVisible, exiting);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: LESSON_MAP_STYLES }} />

      <button
        type="button"
        data-lesson-map-toggle
        data-tour-id={toggleTourId}
        onClick={handleBubbleClick}
        onPointerDown={handleBubblePointerDown}
        onPointerMove={handleBubblePointerMove}
        onPointerUp={finishBubbleDrag}
        onPointerCancel={finishBubbleDrag}
        aria-expanded={open}
        aria-controls={panelId}
        data-bubble-motion={bubbleOffscreen ? 'out' : 'in'}
        style={{
          top: bubbleY,
          transform: bubbleOffscreen
            ? isRtl
              ? 'translate(calc(-100% - 1.25rem), -50%)'
              : 'translate(calc(100% + 1.25rem), -50%)'
            : 'translate(0, -50%)'
        }}
        className={`lesson-map-bubble-btn group fixed z-[58] flex h-9 w-9 cursor-grab items-center justify-center rounded-full p-0 text-indigo-900 hover:scale-[1.06] active:scale-[0.94] dark:text-indigo-100 sm:h-10 sm:w-10 ${
          isRtl ? 'left-2 sm:left-3' : 'right-2 sm:right-3'
        } ${
          bubbleDragging ? 'is-dragging scale-100' : ''
        } ${bubbleOffscreen ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
        title={bubbleTitle}
        aria-label={open ? closeAria : openAria}
        aria-hidden={bubbleOffscreen}
        tabIndex={bubbleOffscreen ? -1 : 0}
      >
        <Map
          className="relative z-[2] h-[15px] w-[15px] drop-shadow-[0_1px_1px_rgba(255,255,255,0.45)] transition-transform duration-200 group-hover:scale-110 sm:h-4 sm:w-4"
          aria-hidden
        />
      </button>

      {panelVisible &&
        createPortal(
          <>
            <div
              className={`lesson-map-backdrop keyboard-slide-modal-backdrop fixed inset-0 z-[59] bg-black/70${
                backdropVisible ? ' is-visible' : ''
              }`}
              aria-hidden
            />

            <aside
              ref={panelRef}
              id={panelId}
              data-tour-id={panelTourId}
              role="dialog"
              aria-modal="true"
              aria-label={navAria}
              dir={direction}
              className={`lesson-map-panel fixed bottom-0 top-16 z-[60] flex w-[min(52vw,200px)] flex-col overflow-hidden bg-white/95 backdrop-blur-md dark:bg-gray-800/95 sm:w-[clamp(260px,30vw,420px)] ${
                isRtl
                  ? 'left-0 border-r border-indigo-200/90 shadow-[12px_0_48px_-12px_rgba(79,70,229,0.22)] dark:border-indigo-900/50 dark:shadow-black/50'
                  : 'right-0 border-l border-indigo-200/90 shadow-[-12px_0_48px_-12px_rgba(79,70,229,0.22)] dark:border-indigo-900/50 dark:shadow-black/50'
              } ${
                exiting
                  ? isRtl
                    ? 'lesson-map-panel-exit-rtl'
                    : 'lesson-map-panel-exit'
                  : isRtl
                    ? 'lesson-map-panel-enter-rtl'
                    : 'lesson-map-panel-enter'
              }`}
            >
              <header
                className="shrink-0 border-b border-indigo-200/90 bg-white/90 px-2 py-2.5 dark:border-indigo-900/40 dark:bg-transparent sm:px-4 sm:py-3.5"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 via-violet-500 to-indigo-600 text-white shadow-md shadow-indigo-500/30 ring-1 ring-indigo-300/35 dark:from-indigo-600 dark:via-violet-600 dark:to-indigo-700 dark:ring-white/10 sm:h-8 sm:w-8"
                        aria-hidden
                      >
                        <Map className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={2.25} />
                      </div>
                      <p className="bg-gradient-to-r from-indigo-700 via-violet-700 to-indigo-600 bg-clip-text text-[11px] font-bold uppercase tracking-wider text-transparent dark:from-indigo-200 dark:via-violet-200 dark:to-indigo-300 sm:text-xs">
                        {panelTitle}
                      </p>
                    </div>
                    {mapData.subtitle ? (
                      <h2 className="mt-1.5 line-clamp-2 text-start text-sm font-bold leading-snug text-gray-900 dark:text-gray-50 sm:mt-2 sm:text-base">
                        <PersianText>{mapData.subtitle}</PersianText>
                      </h2>
                    ) : null}
                    {!loading && totalItems > 0 ? (
                      <div className="mt-2">
                        <div className="flex items-center justify-between gap-2 text-[11px] text-gray-600 dark:text-gray-400 sm:text-xs">
                          <span>
                            {t('lessonMap.completeOf', { completed: completedItems, total: totalItems })}
                          </span>
                          <span className="font-semibold tabular-nums text-indigo-600 dark:text-indigo-300">
                            {progressPercent}%
                          </span>
                        </div>
                        <div
                          className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-indigo-100 dark:bg-indigo-900/45"
                          dir={direction}
                          role="progressbar"
                          aria-valuenow={progressPercent}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label={t('lessonMap.progressAria', { percent: progressPercent })}
                        >
                          <div
                            className={`h-full rounded-full transition-[width] duration-500 ease-out ${mirrorRtlDecor('bg-gradient-to-r from-indigo-500 via-violet-500 to-emerald-500', isRtl)}`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={closePanel}
                    className="shrink-0 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-indigo-100/80 hover:text-gray-800 dark:hover:bg-indigo-900/40 dark:hover:text-gray-200"
                    aria-label={closeAria}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </header>

              <div
                ref={scrollRef}
                className="lesson-map-scroll min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-1.5 py-2.5 sm:px-4 sm:py-4"
              >
                <div ref={mapContentRef} className="relative">
                <LessonMapHereContext.Provider value={hereReady}>
                {animateFocusChange ? (
                  <div
                    ref={focusBoxRef}
                    aria-hidden
                    data-passed="false"
                    className="lesson-map-focus-box pointer-events-none absolute z-[1] rounded-lg border-2 bg-transparent opacity-0 sm:rounded-xl"
                  />
                ) : null}
                {loading ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-16 text-indigo-600 dark:text-indigo-300">
                    <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
                    <p className="text-sm font-medium">{t('lessonMap.loading')}</p>
                  </div>
                ) : error ? (
                  <div className="rounded-xl border border-red-200 bg-red-50/80 p-4 text-center dark:border-red-900/50 dark:bg-red-950/30">
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                    {onRetry ? (
                      <button
                        type="button"
                        onClick={onRetry}
                        className="mt-3 text-sm font-semibold text-indigo-600 hover:underline dark:text-indigo-300"
                      >
                        {t('lessonMap.tryAgain')}
                      </button>
                    ) : null}
                  </div>
                ) : totalItems === 0 ? (
                  <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                    {emptyLabel}
                  </p>
                ) : (
                  <div className="space-y-5">
                    {mapData.sections.map((section, sectionIdx) => (
                      <LessonMapSectionBlock
                        key={section.levelId ?? `section-${sectionIdx}`}
                        section={section}
                        subscriptionPlans={subscriptionPlans}
                        onNavigate={handleNavigate}
                        isRtl={isRtl}
                        depth={0}
                        animateFocusChange={animateFocusChange}
                      />
                    ))}
                  </div>
                )}
                </LessonMapHereContext.Provider>
                </div>
              </div>
            </aside>
          </>,
          document.body
        )}
    </>
  );
};

function LessonMapSectionBlock({
  section,
  subscriptionPlans,
  onNavigate,
  isRtl = false,
  depth = 0,
  animateFocusChange = false
}: {
  section: LessonMapSection;
  subscriptionPlans: SubscriptionPlan[];
  onNavigate: (item: LessonMapItem) => void;
  isRtl?: boolean;
  depth?: number;
  animateFocusChange?: boolean;
}) {
  const { t } = useTranslation('learning');
  const hasItems = section.items.length > 0;
  const hasChildren = (section.children?.length ?? 0) > 0;
  if (!hasItems && !hasChildren) return null;

  const kind = section.sectionKind ?? 'level';
  const HeaderIcon = kind === 'course' ? GraduationCap : kind === 'lesson' ? BookOpen : Layers;
  /** Course/level/lesson quizzes·exercises under the title are collapsible. */
  const itemsCollapsible = hasItems && (kind === 'course' || kind === 'level' || kind === 'lesson');
  const hasCurrentItem = section.items.some((i) => i.isCurrent);
  const [itemsExpanded, setItemsExpanded] = useState(true);

  useEffect(() => {
    if (hasCurrentItem) setItemsExpanded(true);
  }, [hasCurrentItem]);

  const headerIconBoxClass =
    kind === 'course'
      ? 'bg-gradient-to-br from-indigo-500 to-violet-600 shadow-indigo-500/25'
      : kind === 'lesson'
        ? 'bg-gradient-to-br from-sky-500 to-blue-600 shadow-sky-500/25'
        : section.levelLocked
          ? 'bg-gradient-to-br from-gray-400 to-gray-500 shadow-gray-400/20'
          : 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/25';
  const headerTextClass =
    kind === 'course'
      ? 'text-indigo-800 dark:text-indigo-200'
      : kind === 'lesson'
        ? 'text-sky-800 dark:text-sky-200'
        : 'text-emerald-800 dark:text-emerald-200';
  /** Indent nested sections with a spacer so the spine stays in the 18px gutter. */
  const nestSpacerClass =
    depth <= 0 ? '' : depth === 1 ? 'w-2 shrink-0 sm:w-3' : 'w-3 shrink-0 sm:w-4';

  const trailingStatus = (
    <span
      className="flex h-3.5 w-3.5 shrink-0 items-center justify-center sm:h-4 sm:w-4"
      aria-hidden={!(section.levelLocked || section.levelCompleted)}
    >
      {section.levelLocked ? (
        <Lock
          className="h-3 w-3 text-gray-400 dark:text-gray-500 sm:h-3.5 sm:w-3.5"
          aria-label={t('lessonMap.levelLocked')}
        />
      ) : section.levelCompleted ? (
        <CheckCircle2
          className="h-3 w-3 text-green-600 dark:text-green-400 sm:h-3.5 sm:w-3.5"
          aria-label={t('lessonMap.levelComplete')}
        />
      ) : null}
    </span>
  );

  const body = (
    <>
      {section.levelTitle ? (
        <div
          className={`mb-2 flex items-center gap-1.5 text-start sm:gap-2 ${
            section.levelLocked ? 'opacity-70' : ''
          }`}
          dir={isRtl ? 'rtl' : 'ltr'}
        >
          {/* Same spine gutter as quiz/exercise bullets so category icons share that start. */}
          <div className={`relative flex ${SPINE_GUTTER_CLASS} shrink-0 items-center justify-center`}>
            <div
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-white shadow-md sm:h-7 sm:w-7 sm:rounded-xl ${headerIconBoxClass}`}
              aria-hidden
            >
              {section.levelLocked ? (
                <Lock className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              ) : (
                <HeaderIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              )}
            </div>
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-2 pe-2 sm:pe-3">
            <div className="min-w-0 flex-1">
              {itemsCollapsible ? (
                <button
                  type="button"
                  onClick={() => setItemsExpanded((v) => !v)}
                  className="flex w-full min-w-0 items-center gap-1 text-start"
                  aria-expanded={itemsExpanded}
                >
                  <p
                    className={`min-w-0 flex-1 truncate text-[11px] font-bold uppercase tracking-wide sm:text-xs ${headerTextClass}`}
                  >
                    <PersianText>{section.levelTitle}</PersianText>
                  </p>
                  <ChevronDown
                    className={`h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-200 ease-out motion-reduce:transition-none dark:text-gray-400 ${
                      itemsExpanded ? 'rotate-0' : isRtl ? 'rotate-90' : '-rotate-90'
                    }`}
                    aria-hidden
                  />
                </button>
              ) : (
                <p
                  className={`truncate text-[11px] font-bold uppercase tracking-wide sm:text-xs ${headerTextClass}`}
                >
                  <PersianText>{section.levelTitle}</PersianText>
                </p>
              )}
            </div>
            {trailingStatus}
            {section.levelLocked && (section.levelSubscriptionPlanIds?.length ?? 0) > 0 ? (
              <SubscriptionPlanIcons
                planIds={section.levelSubscriptionPlanIds ?? []}
                plans={subscriptionPlans}
                iconSize="p-1.5"
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Smooth collapse for course/level quizzes·exercises under the title. */}
      {hasItems ? (
        <CollapsibleFadePanel expanded={!itemsCollapsible || itemsExpanded}>
            <div className="relative" role="list">
              {section.items.length > 1 ? (
                <div
                  className={`pointer-events-none absolute bottom-3 top-3 flex ${SPINE_GUTTER_CLASS} justify-center ${
                    isRtl ? 'right-0' : 'left-0'
                  }`}
                  aria-hidden
                >
                  <div className="w-0.5 self-stretch rounded-full bg-gradient-to-b from-indigo-300 via-indigo-400/80 to-indigo-300 dark:from-indigo-800 dark:via-indigo-700/60 dark:to-indigo-900" />
                </div>
              ) : null}
              <div className="space-y-1.5 pb-0.5 sm:space-y-2">
                {section.items.map((item) => (
                  <LessonMapRow
                    key={item.id}
                    item={item}
                    subscriptionPlans={subscriptionPlans}
                    onSelect={() => onNavigate(item)}
                    isRtl={isRtl}
                    animateFocusChange={animateFocusChange}
                  />
                ))}
              </div>
            </div>
        </CollapsibleFadePanel>
      ) : null}

      {hasChildren ? (
        <div className={`space-y-3 ${hasItems && (!itemsCollapsible || itemsExpanded) ? 'mt-3' : ''}`}>
          {section.children!.map((child, childIdx) => (
            <LessonMapSectionBlock
              key={child.levelId ?? `child-${depth}-${childIdx}`}
              section={child}
              subscriptionPlans={subscriptionPlans}
              onNavigate={onNavigate}
              isRtl={isRtl}
              depth={depth + 1}
              animateFocusChange={animateFocusChange}
            />
          ))}
        </div>
      ) : null}
    </>
  );

  if (depth <= 0) return <div>{body}</div>;

  return (
    <div className="flex">
      <div className={nestSpacerClass} aria-hidden />
      <div className="min-w-0 flex-1">{body}</div>
    </div>
  );
}

function dotColorClass(item: LessonMapItem): string {
  const passed = item.isCompleted === true;
  if (item.isCurrent && passed) {
    return 'bg-green-500 ring-green-500/30 dark:bg-green-500 dark:ring-green-900';
  }
  if (item.isCurrent) {
    return 'bg-blue-500 ring-blue-500/30 dark:ring-blue-900';
  }
  if (item.isLocked) {
    return 'bg-gray-300 ring-gray-400/35 dark:bg-gray-600 dark:ring-gray-800';
  }
  if (passed) {
    return 'bg-green-300 ring-green-400/30 dark:bg-green-600/70 dark:ring-green-950';
  }
  return 'bg-indigo-200 ring-indigo-400/30 dark:bg-indigo-800 dark:ring-indigo-950';
}

function LessonMapRow({
  item,
  subscriptionPlans,
  onSelect,
  isRtl = false,
  animateFocusChange = false
}: {
  item: LessonMapItem;
  subscriptionPlans: SubscriptionPlan[];
  onSelect: () => void;
  isRtl?: boolean;
  animateFocusChange?: boolean;
}) {
  const { t } = useTranslation('learning');
  const hereReady = useContext(LessonMapHereContext);
  const isCurrent = item.isCurrent;
  const showHereBadge = isCurrent && (!animateFocusChange || hereReady);
  const isLocked = item.isLocked === true;
  const isPassed = item.isCompleted === true;
  const isCurrentPassed = isCurrent && isPassed;
  const disabled = isCurrent || isLocked;
  /** Align dot vertical center with the title. Extra offset waits until the badge has faded in. */
  const dotAlignClass = showHereBadge
    ? 'mt-[27px] sm:mt-[29px]'
    : 'mt-[13px] sm:mt-[15px]';
  const youAreHereBadge = (
    <span
      className={`mb-0.5 inline-flex items-center gap-0.5 rounded-full py-px pl-1.5 pr-1 text-[8px] font-bold uppercase tracking-wide text-white sm:mb-1 sm:gap-1 sm:py-0.5 sm:pl-2 sm:pr-1.5 sm:text-[9px] ${
        isCurrentPassed ? 'bg-green-600 dark:bg-green-500' : 'bg-blue-600 dark:bg-blue-500'
      }`}
    >
      {t('lessonMap.youAreHere')}
      <MapPin className="h-2.5 w-2.5 shrink-0 sm:h-3 sm:w-3" aria-hidden />
    </span>
  );

  const dotGutter = (
    <div className={`relative flex ${SPINE_GUTTER_CLASS} shrink-0 justify-center`}>
      <span
        data-open={showHereBadge ? 'true' : 'false'}
        className={`relative z-10 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ${dotAlignClass} ${dotColorClass(item)} ${
          isCurrent ? 'scale-110' : 'scale-100'
        } ${animateFocusChange ? 'lesson-map-dot-shift' : ''}`}
        aria-hidden
      />
    </div>
  );

  const lessonCard = (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      title={isLocked ? t('lessonMap.subscriptionRequired') : undefined}
      aria-disabled={disabled}
      dir={isRtl ? 'rtl' : 'ltr'}
      data-lesson-map-card
      data-lesson-map-id={item.id}
      data-lesson-map-passed={isPassed ? 'true' : 'false'}
      {...(isCurrent ? { 'data-lesson-map-current-card': 'true' } : {})}
      className={`min-w-0 rounded-lg border px-2 py-1.5 text-start sm:rounded-xl sm:px-3 sm:py-2 ${
        isRtl ? '' : 'flex-1'
      } ${
        animateFocusChange
          ? 'transition-[background-color,border-color,color] duration-200 ease-out motion-reduce:transition-none'
          : 'transition-all duration-200'
      } ${
        isCurrentPassed
          ? animateFocusChange
            ? 'cursor-default border-transparent bg-green-50/90 dark:bg-green-950/40'
            : 'lesson-map-you-are-here-green cursor-default border-green-400 bg-green-50/90 dark:border-green-500/70 dark:bg-green-950/40'
          : isCurrent
            ? animateFocusChange
              ? 'cursor-default border-transparent bg-blue-50/90 dark:bg-blue-950/40'
              : 'lesson-map-you-are-here cursor-default border-blue-400 bg-blue-50/90 dark:border-blue-500/70 dark:bg-blue-950/40'
            : isLocked
              ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-80 dark:border-gray-700/60 dark:bg-gray-900/40 dark:opacity-65'
              : isPassed
                ? 'border-green-200 bg-green-50 hover:border-green-300 hover:bg-green-100/80 dark:border-green-900/40 dark:bg-green-950/20 dark:hover:border-green-800'
                : 'border-indigo-200/90 bg-white hover:border-indigo-300 hover:bg-indigo-50 dark:border-indigo-900/40 dark:bg-gray-900/50 dark:hover:border-indigo-700 dark:hover:bg-indigo-950/30'
      }`}
    >
      <div className={`min-w-0 ${animateFocusChange ? 'relative z-[2]' : ''}`}>
        {animateFocusChange ? (
          <div
            data-lesson-map-here-slot
            data-open={showHereBadge ? 'true' : 'false'}
            className={`lesson-map-here-slot grid motion-reduce:transition-none ${
              showHereBadge ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
            }`}
            aria-hidden={!showHereBadge}
          >
            <div data-lesson-map-here-inner className="min-h-0 overflow-hidden">{youAreHereBadge}</div>
          </div>
        ) : isCurrent ? (
          youAreHereBadge
        ) : null}
        <div className="flex items-center gap-1">
          <p
            className={`min-w-0 flex-1 text-[11px] font-semibold leading-snug sm:text-sm ${
              animateFocusChange ? 'transition-colors duration-150 ease-out motion-reduce:transition-none' : ''
            } ${
              isCurrentPassed
                ? 'text-green-900 dark:text-green-100'
                : isCurrent
                  ? 'text-blue-900 dark:text-blue-100'
                  : isLocked
                    ? 'text-gray-600 dark:text-gray-400'
                    : isPassed
                      ? 'text-green-900 dark:text-green-100'
                      : 'text-gray-800 dark:text-gray-100'
            }`}
          >
            <PersianText>{item.title}</PersianText>
          </p>
          {isLocked ? (
            <Lock className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
          ) : isPassed ? (
            <CheckCircle2
              className={`h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5 ${
                animateFocusChange ? 'transition-colors duration-150 ease-out motion-reduce:transition-none' : ''
              } ${
                isCurrentPassed
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-green-600 dark:text-green-400'
              }`}
              aria-label={t('lessonMap.complete')}
            />
          ) : null}
        </div>
        {item.subtitle ? (
          <p className="mt-0.5 line-clamp-2 text-[10px] text-gray-600 dark:text-gray-400 sm:text-xs">
            <PersianText>{item.subtitle}</PersianText>
          </p>
        ) : null}
        {isLocked && (item.subscriptionPlanIds?.length ?? 0) > 0 ? (
          <div className="mt-1" onClick={(e) => e.stopPropagation()}>
            <SubscriptionPlanIcons
              planIds={item.subscriptionPlanIds ?? []}
              plans={subscriptionPlans}
              iconSize="p-1.5"
            />
          </div>
        ) : null}
      </div>
    </button>
  );

  if (isRtl) {
    return (
      <div
        className="grid grid-cols-[minmax(0,1fr)_18px] gap-1.5 sm:gap-2"
        data-lesson-map-current={isCurrent ? 'true' : undefined}
        role="listitem"
        dir="ltr"
      >
        {lessonCard}
        {dotGutter}
      </div>
    );
  }

  return (
    <div
      className="flex gap-1.5 sm:gap-2"
      data-lesson-map-current={isCurrent ? 'true' : undefined}
      role="listitem"
    >
      {dotGutter}
      {lessonCard}
    </div>
  );
}
